import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, BookTemplate, Play, Clock, Layers, Shield, Zap, ChevronRight, GitBranch, Pencil, Trash2 } from 'lucide-react';
import axios from 'axios';
import { API_BASE } from '@/lib/api';
import { toast } from 'sonner';
import TemplateEditorComponent from '@/components/templates/TemplateEditor';

const CATEGORY_COLORS = {
  nca: 'bg-blue-500/15 text-blue-400', popk: 'bg-purple-500/15 text-purple-400',
  pkpd: 'bg-indigo-500/15 text-indigo-400', er: 'bg-teal-500/15 text-teal-400',
  special_pop: 'bg-amber-500/15 text-amber-400', ddi: 'bg-orange-500/15 text-orange-400',
  simulation: 'bg-cyan-500/15 text-cyan-400', precision: 'bg-green-500/15 text-green-400',
  pbpk: 'bg-rose-500/15 text-rose-400', reports: 'bg-emerald-500/15 text-emerald-400',
  study_design: 'bg-violet-500/15 text-violet-400',
};

const DIFFICULTY_COLORS = {
  beginner: 'bg-green-500/15 text-green-400', intermediate: 'bg-amber-500/15 text-amber-400',
  advanced: 'bg-red-500/15 text-red-400',
};

const TIER_ICONS = { free: null, pro: Zap, team: Shield };

export default function TemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('all');
  const [diffFilter, setDiffFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [myTemplates, setMyTemplates] = useState([]);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const navigate = useNavigate();

  useEffect(() => { loadTemplates(); loadMyTemplates(); }, [catFilter, diffFilter]);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const params = {};
      if (catFilter !== 'all') params.category = catFilter;
      if (diffFilter !== 'all') params.difficulty = diffFilter;
      const res = await axios.get(`${API_BASE}/workflow-templates`, { params });
      setTemplates(res.data.templates || []);
      if (res.data.categories) setCategories(res.data.categories);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const loadMyTemplates = async () => {
    try {
      const res = await axios.get(`${API_BASE}/workflow-templates/my`);
      setMyTemplates(res.data.templates || []);
    } catch (e) { /* ignore - not logged in */ }
  };

  const deleteMyTemplate = async (id) => {
    if (!window.confirm('Delete this custom template?')) return;
    try {
      await axios.delete(`${API_BASE}/workflow-templates/${id}`);
      toast.success('Template deleted');
      loadMyTemplates();
    } catch (e) { toast.error('Delete failed'); }
  };

  const handleSearch = useCallback(async () => {
    if (!search.trim()) { loadTemplates(); return; }
    setLoading(true);
    try {
      const params = { search: search.trim() };
      if (catFilter !== 'all') params.category = catFilter;
      const res = await axios.get(`${API_BASE}/workflow-templates`, { params });
      setTemplates(res.data.templates || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [search, catFilter]);

  useEffect(() => {
    const timer = setTimeout(handleSearch, 300);
    return () => clearTimeout(timer);
  }, [search, handleSearch]);

  const catList = ['all', ...Object.keys(categories)];

  return (
    <div className="p-4 sm:p-6 md:p-8 max-w-[1200px] mx-auto">
      <div className="mb-4 sm:mb-6">
        <h1 className="text-xl sm:text-2xl font-bold tracking-tight" style={{ fontFamily: 'Space Grotesk' }}>Template Library</h1>
        <p className="text-sm text-muted-foreground mt-1">{templates.length} workflow templates for pharmacometric analyses</p>
      </div>

      {/* Search */}
      <div className="relative mb-3 sm:mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search templates..."
          className="pl-9 h-10 sm:h-11 rounded-xl" data-testid="template-search-input" />
      </div>

      {/* Filters */}
      <div className="flex gap-3 sm:gap-4 mb-4 sm:mb-6 flex-wrap">
        {/* Category pills */}
        <div className="flex gap-1.5 flex-wrap flex-1 overflow-x-auto pb-1">
          {catList.map(c => {
            const info = categories[c] || {};
            return (
              <Button key={c} size="sm" variant={catFilter === c ? 'default' : 'outline'}
                onClick={() => setCatFilter(c)}
                className={`rounded-full h-7 text-[10px] px-2.5 ${catFilter === c && c !== 'all' ? CATEGORY_COLORS[c] || '' : ''}`}>
                {c === 'all' ? 'All' : info.label || c}
              </Button>
            );
          })}
        </div>
        {/* Difficulty filter */}
        <Select value={diffFilter} onValueChange={setDiffFilter}>
          <SelectTrigger className="w-36 h-8 rounded-lg text-xs">
            <SelectValue placeholder="Difficulty" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Levels</SelectItem>
            <SelectItem value="beginner">Beginner</SelectItem>
            <SelectItem value="intermediate">Intermediate</SelectItem>
            <SelectItem value="advanced">Advanced</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* My Templates (forked) */}
      {myTemplates.length > 0 && (
        <div className="mb-8">
          <h2 className="text-lg font-semibold mb-3" style={{ fontFamily: 'Space Grotesk' }}>My Templates</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {myTemplates.map((t, i) => (
              <Card key={t.id} className="card-hover">
                <CardContent className="py-3 px-4">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[9px] px-2 py-0.5 rounded-full bg-violet-500/15 text-violet-400">Forked</span>
                  </div>
                  <h3 className="text-sm font-semibold" style={{ fontFamily: 'Space Grotesk' }}>{t.name}</h3>
                  <p className="text-[10px] text-muted-foreground mt-0.5">From: {t.forked_from}</p>
                  <div className="flex gap-1.5 mt-2">
                    <Button size="sm" variant="outline" className="rounded-full h-7 text-[10px] px-2.5"
                      onClick={() => setEditingTemplate(t)}>
                      <Pencil className="w-3 h-3 mr-1" /> Edit
                    </Button>
                    <Button size="sm" variant="ghost" className="rounded-full h-7 text-[10px] px-2.5 text-destructive"
                      onClick={() => deleteMyTemplate(t.id)}>
                      <Trash2 className="w-3 h-3 mr-1" /> Delete
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Template grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1,2,3,4,5,6].map(i => <Skeleton key={i} className="h-44 rounded-xl" />)}
        </div>
      ) : templates.length === 0 ? (
        <div className="text-center py-16">
          <BookTemplate className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">No templates match your filters</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map((t, i) => {
            const TierIcon = TIER_ICONS[t.tier_required];
            return (
              <motion.div key={t.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(i * 0.03, 0.3) }}>
                <Card className="card-hover h-full cursor-pointer" onClick={async () => {
                  try {
                    const res = await axios.get(`${API_BASE}/workflow-templates/detail/${t.id}`);
                    setSelected(res.data);
                  } catch { setSelected(t); }
                }}
                  data-testid={`template-card-${i}`}>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className={`text-[9px] font-semibold px-2 py-0.5 rounded-full ${CATEGORY_COLORS[t.category] || 'bg-muted text-muted-foreground'}`}>
                          {categories[t.category]?.label || t.category}
                        </span>
                        <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${DIFFICULTY_COLORS[t.difficulty] || ''}`}>
                          {t.difficulty}
                        </span>
                        {TierIcon && (
                          <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-primary/10 text-primary flex items-center gap-0.5">
                            <TierIcon className="w-2.5 h-2.5" /> {t.tier_required}
                          </span>
                        )}
                      </div>
                    </div>
                    <CardTitle className="text-sm mt-2 leading-snug" style={{ fontFamily: 'Space Grotesk' }}>{t.name}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2">{t.description}</p>
                    <div className="flex items-center gap-3 mt-3 text-[10px] text-muted-foreground">
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {t.duration}</span>
                      <span className="flex items-center gap-1"><Layers className="w-3 h-3" /> {t.steps_count} steps</span>
                    </div>
                    {t.tags?.length > 0 && (
                      <div className="flex gap-1 mt-2 flex-wrap">
                        {t.tags.slice(0, 3).map(tag => (
                          <span key={tag} className="text-[9px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{tag}</span>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Template Detail / Launch Modal */}
      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <div className="flex items-center gap-2 mb-1">
              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${CATEGORY_COLORS[selected?.category] || ''}`}>
                {categories[selected?.category]?.label || selected?.category}
              </span>
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${DIFFICULTY_COLORS[selected?.difficulty] || ''}`}>
                {selected?.difficulty}
              </span>
              <span className="text-[10px] text-muted-foreground">{selected?.duration}</span>
            </div>
            <DialogTitle style={{ fontFamily: 'Space Grotesk' }}>{selected?.name}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">{selected?.description}</p>

          {/* Steps visualization */}
          <div className="mt-4 space-y-2">
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Workflow Steps</h4>
            {selected?.steps?.map((step, i) => (
              <div key={i} className="flex items-center gap-3 text-xs">
                <span className="w-6 h-6 rounded-full bg-muted flex items-center justify-center text-[10px] font-bold flex-shrink-0">
                  {i + 1}
                </span>
                <div className="flex-1">
                  <span className="font-medium">{step.description}</span>
                  <span className="text-muted-foreground ml-1.5 text-[10px]">({step.agent})</span>
                </div>
                {step.human_review && (
                  <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-400">Review</span>
                )}
              </div>
            )) || (
              <p className="text-xs text-muted-foreground">{selected?.steps_count} steps</p>
            )}
          </div>

          {/* Parameters */}
          {selected?.parameters?.length > 0 && (
            <div className="mt-4 space-y-2">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Parameters</h4>
              {selected.parameters.map((p, i) => (
                <div key={i} className="flex items-center justify-between text-xs">
                  <span>{p.label || p.name}</span>
                  <span className="font-mono text-muted-foreground">{p.default}</span>
                </div>
              ))}
            </div>
          )}

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setSelected(null)} className="rounded-full text-xs">Cancel</Button>
            <Button variant="outline" className="rounded-full text-xs" onClick={async () => {
              try {
                const res = await axios.post(`${API_BASE}/workflow-templates/${selected?.id}/fork`);
                toast.success(`Forked "${selected?.name}" — you can now customize it`);
                setSelected(null);
                // Reload to show in My Templates
                loadMyTemplates();
              } catch (e) { toast.error('Fork failed: ' + (e.response?.data?.detail || e.message)); }
            }}>
              <GitBranch className="w-3.5 h-3.5 mr-1" /> Fork & Customize
            </Button>
            <Button className="rounded-full text-xs" onClick={() => {
              toast.success(`Template "${selected?.name}" ready — launch from a project workspace`);
              setSelected(null);
              navigate('/app/projects');
            }}>
              <Play className="w-3.5 h-3.5 mr-1" /> Launch Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Template Editor Dialog */}
      <Dialog open={!!editingTemplate} onOpenChange={() => setEditingTemplate(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Space Grotesk' }}>Edit Template</DialogTitle>
          </DialogHeader>
          {editingTemplate && (
            <TemplateEditorComponent
              template={editingTemplate}
              onSave={(updated) => { setEditingTemplate(null); loadMyTemplates(); }}
              onClose={() => setEditingTemplate(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
